import './palette.scss';

import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';

import Kbd from '@/components/reusable/kbd';
import { pageurl } from '@/constants/pageurl';
import { useRoleActionContext } from '@/context/role-actions';
import type { IHelpHighlight } from '@/hooks/queries/help-centre/help-centre.types';
import { useHelpCapabilities, useHelpSearch } from '@/hooks/queries/help-centre/use-help-queries';
import { menuData } from '@/layout/menu/components/data';

import { searchUrl } from '../components/SearchField';
import { HC_COPY } from '../HelpCentre.Copy';
import { Highlight } from '../highlight';
import { useHelpSessionReset } from '../use-help-session-reset';
import { paletteStore, usePaletteOpen } from './palette-store';
import { clearRecent, isPaletteShortcut, matchPages, pushRecent, readRecent, visiblePages } from './palette-utils';

type Option =
	| { kind: 'recent'; id: string; label: string }
	| { kind: 'guide'; id: string; label: string; slug: string; module: string; snippet: string; highlights: IHelpHighlight[] }
	| { kind: 'all-guides'; id: string; label: string }
	| { kind: 'page'; id: string; label: string; trail: string | null; path: string };

const GUIDE_LIMIT = 6;

/**
 * ⌘K / Ctrl-K. A hand-built ARIA 1.2 combobox (input + listbox with two `group`s) in a modal
 * dialog — no dependency. Focus never leaves the input: arrows move `aria-activedescendant`,
 * Enter activates, Escape closes and returns focus to whatever opened the palette.
 *
 * "Go to" is built from `buildVisibleMenu`, so it can only offer pages the sidebar would show.
 * "Guides" is the server's permission-filtered search and disappears when the capability
 * handshake says there is no Help Centre on this server.
 */
const PaletteDialog = () => {
	const navigate = useNavigate();
	const { snapshot } = useRoleActionContext();
	const caps = useHelpCapabilities();
	const [q, setQ] = useState('');
	const [activeIndex, setActiveIndex] = useState(0);
	const [recent, setRecent] = useState<string[]>(readRecent);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const baseId = useId();

	const search = useHelpSearch(q, { pageSize: GUIDE_LIMIT, enabled: caps.available });
	const pages = useMemo(() => visiblePages(menuData, snapshot), [snapshot]);

	const query = q.trim();

	const groups = useMemo(() => {
		const out: { id: string; label: string; options: Option[] }[] = [];
		if (!query) {
			if (recent.length > 0) {
				out.push({
					id: 'recent',
					label: HC_COPY.palette.recent,
					options: recent.map((r, i) => ({ kind: 'recent', id: `${baseId}-recent-${i}`, label: r })),
				});
			}
			return out;
		}
		if (caps.available && search.data && !search.isSettling) {
			const guides: Option[] = search.data.items.slice(0, GUIDE_LIMIT).map((h, i) => ({
				kind: 'guide',
				id: `${baseId}-guide-${i}`,
				label: h.title,
				slug: h.slug,
				module: h.moduleTitle,
				snippet: h.snippet,
				highlights: h.highlights,
			}));
			if (search.data.totalCount > guides.length) {
				guides.push({ kind: 'all-guides', id: `${baseId}-all`, label: `See all ${search.data.totalCount} guides` });
			}
			if (guides.length > 0) out.push({ id: 'guides', label: HC_COPY.palette.guides, options: guides });
		}
		const pageOptions: Option[] = matchPages(query, pages).map((p, i) => ({
			kind: 'page',
			id: `${baseId}-page-${i}`,
			label: p.title,
			trail: p.trail,
			path: p.path,
		}));
		if (pageOptions.length > 0) out.push({ id: 'pages', label: HC_COPY.palette.goTo, options: pageOptions });
		return out;
	}, [query, recent, caps.available, search.data, search.isSettling, pages, baseId]);

	const options = useMemo(() => groups.flatMap((g) => g.options), [groups]);
	const active = options[Math.min(activeIndex, options.length - 1)] ?? null;

	// Reset to the first option whenever the result set changes shape.
	useEffect(() => setActiveIndex(0), [query, options.length]);

	useEffect(() => {
		const opener = document.activeElement as HTMLElement | null;
		inputRef.current?.focus();
		return () => opener?.focus?.();
	}, []);

	useEffect(() => {
		if (!active) return;
		document.getElementById(active.id)?.scrollIntoView?.({ block: 'nearest' });
	}, [active]);

	const close = () => paletteStore.close();

	const activate = (option: Option | null) => {
		if (!option) {
			if (query.length >= 2 && caps.available) {
				setRecent(pushRecent(query));
				navigate(searchUrl(query));
				close();
			}
			return;
		}
		switch (option.kind) {
			case 'recent':
				setQ(option.label);
				return;
			case 'guide':
				setRecent(pushRecent(query));
				navigate(`${pageurl.HELP}/${encodeURIComponent(option.slug)}`);
				break;
			case 'all-guides':
				setRecent(pushRecent(query));
				navigate(searchUrl(query));
				break;
			case 'page':
				navigate(option.path);
				break;
		}
		close();
	};

	const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
		const count = options.length;
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				if (count) setActiveIndex((i) => (i + 1) % count);
				break;
			case 'ArrowUp':
				e.preventDefault();
				if (count) setActiveIndex((i) => (i - 1 + count) % count);
				break;
			case 'Home':
				if (count && e.ctrlKey) {
					e.preventDefault();
					setActiveIndex(0);
				}
				break;
			case 'End':
				if (count && e.ctrlKey) {
					e.preventDefault();
					setActiveIndex(count - 1);
				}
				break;
			case 'Enter':
				e.preventDefault();
				activate(active);
				break;
			case 'Escape':
				// Stop here: the right panel's document-level Escape must not also close a panel underneath.
				e.preventDefault();
				e.stopPropagation();
				close();
				break;
			case 'Tab':
				// The input is the dialog's only stop; keep focus inside the modal (and away from an open
				// right panel's own Tab trap, which would otherwise pull focus behind the palette).
				e.preventDefault();
				e.stopPropagation();
				break;
		}
	};

	const listboxId = `${baseId}-listbox`;
	const searching = caps.available && query.length >= 2 && (search.isSettling || (search.isFetching && !search.data));
	const showGuideMiss = caps.available && query.length >= 2 && !searching && search.data?.items.length === 0 && !search.isError;
	const showPageMiss = query.length > 0 && !groups.some((g) => g.id === 'pages');

	return (
		<div
			className='hc-palette-backdrop'
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) close();
			}}
		>
			<div
				className='hc-palette'
				role='dialog'
				aria-modal='true'
				aria-label={HC_COPY.palette.label}
			>
				<div className='hc-palette__field'>
					<svg
						className='hc-palette__icon'
						width='18'
						height='18'
						viewBox='0 0 24 24'
						fill='none'
						stroke='currentColor'
						strokeWidth='2'
						strokeLinecap='round'
						aria-hidden='true'
						focusable='false'
					>
						<circle
							cx='11'
							cy='11'
							r='7'
						/>
						<path d='M20 20l-3.5-3.5' />
					</svg>
					<input
						ref={inputRef}
						data-palette-input
						className='hc-palette__input'
						role='combobox'
						aria-expanded={options.length > 0}
						aria-controls={listboxId}
						aria-autocomplete='list'
						aria-activedescendant={active?.id}
						aria-label={HC_COPY.palette.label}
						placeholder={HC_COPY.palette.placeholder}
						value={q}
						onChange={(e) => setQ(e.target.value)}
						onKeyDown={onKeyDown}
						autoComplete='off'
						spellCheck={false}
					/>
				</div>

				<div
					ref={listRef}
					id={listboxId}
					role='listbox'
					aria-label={HC_COPY.palette.label}
					className='hc-palette__list'
				>
					{groups.map((g) => (
						<div
							key={g.id}
							role='group'
							aria-labelledby={`${baseId}-${g.id}-label`}
							className='hc-palette__group'
						>
							<div
								id={`${baseId}-${g.id}-label`}
								className='hc-palette__group-label'
								role='presentation'
							>
								<span>{g.label}</span>
								{g.id === 'recent' ? (
									<button
										type='button'
										className='hc-palette__clear'
										tabIndex={-1}
										onMouseDown={(e) => e.preventDefault()}
										onClick={() => {
											clearRecent();
											setRecent([]);
											inputRef.current?.focus();
										}}
									>
										{HC_COPY.palette.clearRecent}
									</button>
								) : null}
							</div>
							{g.options.map((o) => {
								const index = options.indexOf(o);
								const isActive = active?.id === o.id;
								return (
									<div
										key={o.id}
										id={o.id}
										role='option'
										aria-selected={isActive}
										className={`hc-palette__option ${isActive ? 'hc-palette__option--active' : ''}`}
										onMouseMove={() => index !== activeIndex && setActiveIndex(index)}
										onMouseDown={(e) => e.preventDefault()}
										onClick={() => activate(o)}
									>
										<span className='hc-palette__option-title'>{o.label}</span>
										{o.kind === 'guide' ? (
											<>
												{o.module ? <span className='hc-tag'>{o.module}</span> : null}
												{o.snippet ? (
													<span className='hc-palette__snippet'>
														<Highlight
															snippet={o.snippet}
															highlights={o.highlights}
														/>
													</span>
												) : null}
											</>
										) : null}
										{o.kind === 'page' && o.trail ? <span className='hc-palette__trail'>{o.trail}</span> : null}
									</div>
								);
							})}
						</div>
					))}

					<div
						className='hc-palette__status'
						role='status'
						aria-live='polite'
					>
						{searching ? HC_COPY.palette.searching : null}
						{showGuideMiss ? <span>{HC_COPY.palette.noGuides}</span> : null}
						{caps.isError && query ? <span>{HC_COPY.palette.guidesUnavailable}</span> : null}
						{showPageMiss && !searching && options.length === 0 ? <span>{HC_COPY.palette.noPages}</span> : null}
					</div>
				</div>

				<div
					className='hc-palette__footer'
					aria-hidden='true'
				>
					<span>
						<Kbd>↑</Kbd>
						<Kbd>↓</Kbd> {HC_COPY.palette.hintNav}
					</span>
					<span>
						<Kbd>↵</Kbd> {HC_COPY.palette.hint}
					</span>
					<span>
						<Kbd>esc</Kbd> {HC_COPY.palette.hintClose}
					</span>
				</div>
			</div>
		</div>
	);
};

/** Mounted once in the layout. Owns the global shortcut; renders the dialog only while open. */
const CommandPalette = () => {
	const open = usePaletteOpen();
	// Mounted once per session, so it is also where the Help Centre cache is tied to the identity.
	useHelpSessionReset();

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (!isPaletteShortcut(e)) return;
			e.preventDefault();
			paletteStore.toggle();
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, []);

	return open ? createPortal(<PaletteDialog />, document.body) : null;
};

export default CommandPalette;
